import type { SessionLogEntry, SessionStats, SessionType, CompletionStatus, TaskSession } from '../types';
import { Storage } from './storage.ts';

const SESSION_LOG_KEY = 'kf-session-log';
const LEGACY_SESSION_LOG_KEY = 'kairofocus_session_log';

const migrateIfNeeded = (): void => {
  if (!localStorage.getItem(SESSION_LOG_KEY) && localStorage.getItem(LEGACY_SESSION_LOG_KEY)) {
    const raw = localStorage.getItem(LEGACY_SESSION_LOG_KEY)!;
    localStorage.setItem(SESSION_LOG_KEY, raw);
    localStorage.removeItem(LEGACY_SESSION_LOG_KEY);
  }
};

export const addSessionToLog = (
  sessionId: string,
  sessionType: SessionType,
  startTime: number,
  endTime: number,
  plannedDuration: number,
  actualDuration: number,
  completionStatus: CompletionStatus,
  taskId?: string
): void => {
  migrateIfNeeded();
  const logs = getSessionLogs();
  const date = new Date(startTime).toISOString().split('T')[0];
  const entry: SessionLogEntry = {
    sessionId,
    taskId,
    sessionType,
    startTime,
    endTime,
    plannedDuration,
    actualDuration,
    completionStatus,
    date,
  };
  Storage.write(SESSION_LOG_KEY, [...logs, entry]);
};

export const getSessionLogs = (): SessionLogEntry[] => {
  migrateIfNeeded();
  return Storage.read<SessionLogEntry[]>(SESSION_LOG_KEY, []);
};

export const getSessionStats = (): SessionStats => {
  const logs = getSessionLogs();
  const today = new Date().toISOString().split('T')[0];

  const stats: SessionStats = {
    totalSessions: logs.length,
    completedSessions: 0,
    interruptedSessions: 0,
    totalTimeSpent: 0,
    sessionsByType: {
      pomodoro: { count: 0, totalTime: 0, completed: 0 },
      shortBreak: { count: 0, totalTime: 0, completed: 0 },
      longBreak: { count: 0, totalTime: 0, completed: 0 },
    },
    sessionsToday: 0,
    timeSpentToday: 0,
  };

  logs.forEach(log => {
    if (log.completionStatus === 'completed') {
      stats.completedSessions++;
    } else {
      stats.interruptedSessions++;
    }
    stats.totalTimeSpent += log.actualDuration;

    const typeStats = stats.sessionsByType[log.sessionType];
    typeStats.count++;
    typeStats.totalTime += log.actualDuration;
    if (log.completionStatus === 'completed') typeStats.completed++;

    if (log.date === today) {
      stats.sessionsToday++;
      stats.timeSpentToday += log.actualDuration;
    }
  });

  return stats;
};

export const getSessionsByDate = (date: string): SessionLogEntry[] =>
  getSessionLogs().filter(log => log.date === date);

export const getSessionsByType = (sessionType: SessionType): SessionLogEntry[] =>
  getSessionLogs().filter(log => log.sessionType === sessionType);

export const clearSessionLogs = (): void => {
  Storage.remove(SESSION_LOG_KEY);
};

const PRUNE_DAYS = 180;

export const pruneSessionLog = (): void => {
  const cutoff = Date.now() - PRUNE_DAYS * 24 * 60 * 60 * 1000;
  const log = Storage.read<SessionLogEntry[]>(SESSION_LOG_KEY, []);
  const pruned = log.filter(e => e.startTime >= cutoff);
  if (pruned.length < log.length) {
    Storage.write(SESSION_LOG_KEY, pruned);
    const taskSessions = Storage.read<TaskSession[]>('kf-task-sessions', []);
    Storage.write('kf-task-sessions', taskSessions.filter(s => s.startTime >= cutoff));
  }
};
